import { httpClient } from '@/shared/api';
import type {
  TuitionBenchmarkStatus,
  TuitionFeeBenchmarkApiParams,
  TuitionFeeBenchmarkDistribution,
  TuitionFeeBenchmarkFilterParams,
  TuitionFeeBenchmarkMatrixCell,
  TuitionFeeBenchmarkResult,
  TuitionFeeBenchmarkSchoolRow,
  TuitionFeeBenchmarkSummary,
} from './tuitionFeeBenchmark.dto';

const PREFIX = '/app/tuition-fee-benchmark/tuition-fee-benchmark';

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readNullableNum(o: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

function normalizeBenchmarkStatus(raw: string): TuitionBenchmarkStatus {
  const s = raw.trim().toLowerCase();
  if (s === 'above') return 'above';
  if (s === 'below') return 'below';
  return 'near';
}

function mapSummary(raw: unknown): TuitionFeeBenchmarkSummary {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const regional = readNullableNum(o, ['regionalBenchmarkUsd', 'RegionalBenchmarkUsd']);
  return {
    nationalBenchmarkUsd: readNum(o, ['nationalBenchmarkUsd', 'NationalBenchmarkUsd']) ?? 0,
    regionalBenchmarkUsd: regional,
    schoolCount: readNum(o, ['schoolCount', 'SchoolCount']) ?? 0,
    aboveRegionalCount: readNum(o, ['aboveRegionalCount', 'AboveRegionalCount']) ?? 0,
    belowRegionalCount: readNum(o, ['belowRegionalCount', 'BelowRegionalCount']) ?? 0,
    nearRegionalCount: readNum(o, ['nearRegionalCount', 'NearRegionalCount']) ?? 0,
  };
}

function mapDistribution(raw: unknown): TuitionFeeBenchmarkDistribution {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    min: readNum(o, ['min', 'Min']) ?? 0,
    p25: readNum(o, ['p25', 'P25']) ?? 0,
    median: readNum(o, ['median', 'Median']) ?? 0,
    p75: readNum(o, ['p75', 'P75']) ?? 0,
    max: readNum(o, ['max', 'Max']) ?? 0,
  };
}

function mapRow(raw: unknown): TuitionFeeBenchmarkSchoolRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const schoolId = readStr(o, ['schoolId', 'SchoolId']);
  if (!schoolId) return null;

  const programId = readStr(o, ['programId', 'ProgramId', 'gradeProgramId', 'GradeProgramId']);
  const programTitle = readStr(o, ['programTitle', 'ProgramTitle', 'gradeProgramTitle', 'GradeProgramTitle']);

  return {
    schoolId,
    schoolCode: readStr(o, ['schoolCode', 'SchoolCode']),
    schoolName: readStr(o, ['schoolName', 'SchoolName']),
    governorateId: readNum(o, ['governorateId', 'GovernorateId', 'governerateId', 'GovernerateId']) ?? 0,
    governorateName: readStr(o, ['governorateName', 'GovernorateName', 'governerateName', 'GovernerateName']),
    cityName: readStr(o, ['cityName', 'CityName']),
    areaName: readStr(o, ['areaName', 'AreaName']),
    gradeProgramId: programId,
    gradeProgramTitle: programTitle,
    studentsInGrade: readNum(o, ['studentsCount', 'StudentsCount', 'studentsInGrade', 'StudentsInGrade']) ?? 0,
    tuitionPerStudentUsd: readNum(o, ['tuitionPerStudentUsd', 'TuitionPerStudentUsd']) ?? 0,
    regionalBenchmarkUsd: readNum(o, ['regionalBenchmarkUsd', 'RegionalBenchmarkUsd']) ?? 0,
    nationalBenchmarkUsd: readNum(o, ['nationalBenchmarkUsd', 'NationalBenchmarkUsd']) ?? 0,
    varianceRegionalPct: readNum(o, ['varianceRegionalPct', 'VarianceRegionalPct']) ?? 0,
    varianceNationalPct: readNum(o, ['varianceNationalPct', 'VarianceNationalPct']) ?? 0,
    benchmarkStatus: normalizeBenchmarkStatus(readStr(o, ['benchmarkStatus', 'BenchmarkStatus'])),
  };
}

function mapMatrixCell(raw: unknown): TuitionFeeBenchmarkMatrixCell | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const regionId = readStr(o, ['regionId', 'RegionId']);
  if (!regionId) return null;

  const programId = readStr(o, ['programId', 'ProgramId', 'gradeProgramId', 'GradeProgramId']);
  const programTitle = readStr(o, ['programTitle', 'ProgramTitle', 'gradeProgramTitle', 'GradeProgramTitle']);

  return {
    regionId,
    regionName: readStr(o, ['regionName', 'RegionName']),
    gradeProgramId: programId,
    gradeProgramTitle: programTitle,
    avgTuitionUsd: readNum(o, ['avgTuitionUsd', 'AvgTuitionUsd']) ?? 0,
    studentsCount: readNum(o, ['studentsCount', 'StudentsCount']) ?? 0,
    schoolsCount: readNum(o, ['schoolsCount', 'SchoolsCount']) ?? 0,
    varianceVsNationalPct: readNum(o, ['varianceVsNationalPct', 'VarianceVsNationalPct']) ?? 0,
  };
}

export function mapTuitionFeeBenchmarkResult(raw: unknown): TuitionFeeBenchmarkResult {
  const o = unwrapPayload(raw) ?? {};

  const rawRows = (o.rows ?? o.Rows) as unknown[] | undefined;
  const rows = Array.isArray(rawRows)
    ? rawRows.map(mapRow).filter((r): r is TuitionFeeBenchmarkSchoolRow => r != null)
    : [];

  const rawMatrix = (o.regionalMatrix ?? o.RegionalMatrix) as unknown[] | undefined;
  const regionalMatrix = Array.isArray(rawMatrix)
    ? rawMatrix.map(mapMatrixCell).filter((c): c is TuitionFeeBenchmarkMatrixCell => c != null)
    : [];

  return {
    summary: mapSummary(o.summary ?? o.Summary),
    distribution: mapDistribution(o.distribution ?? o.Distribution),
    rows,
    regionalMatrix,
  };
}

function buildQuery(params: TuitionFeeBenchmarkApiParams): string {
  const q = new URLSearchParams();
  q.set('Year', String(params.year));
  q.set('ProgramId', params.programId.trim());
  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    q.set('GovernorateId', String(params.governorateId));
  }
  if (params.cityId != null && Number.isFinite(params.cityId)) {
    q.set('CityId', String(params.cityId));
  }
  if (params.areaId != null && Number.isFinite(params.areaId)) {
    q.set('AreaId', String(params.areaId));
  }
  return q.toString();
}

export function filterParamsToTuitionFeeBenchmarkApiParams(
  params: TuitionFeeBenchmarkFilterParams
): TuitionFeeBenchmarkApiParams | null {
  const year = parseInt(params.year.trim(), 10);
  const programId = params.gradeProgramId.trim();
  if (!Number.isFinite(year) || !programId) return null;

  const apiParams: TuitionFeeBenchmarkApiParams = { year, programId };

  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    apiParams.governorateId = params.governorateId;
  }
  if (params.cityId != null && Number.isFinite(params.cityId)) {
    apiParams.cityId = params.cityId;
  }
  if (params.areaId != null && Number.isFinite(params.areaId)) {
    apiParams.areaId = params.areaId;
  }

  return apiParams;
}

export const tuitionFeeBenchmarkApi = {
  getTuitionFeeBenchmark: async (params: TuitionFeeBenchmarkApiParams): Promise<TuitionFeeBenchmarkResult> => {
    const query = buildQuery(params);
    const res = await httpClient.get<unknown>(`${PREFIX}?${query}`);
    return mapTuitionFeeBenchmarkResult(res);
  },
};
